import * as React from 'react'
import {
  Box,
  TextField,
  Typography,
  Paper,
  Autocomplete,
  Button,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Select,
  SelectChangeEvent,
  Checkbox,
  MenuItem,
  FormControl,
  FormControlLabel
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers'
import debounce from 'lodash/debounce'
import dayjs from 'dayjs'
import { useStore } from 'effector-react'
import { createStore, createEvent, sample } from 'effector'
import { $userSearch, searchUserFx } from '../../../Api/userSearch'
import { $clientSearch, searchClientFx } from '../../../Api/clientSearch'
import { $taskTypes, getTaskTypesFx } from '../../../Api/taskTypes'
import { $mainTable, getMainTableFx } from '../../../Api/PaidService/search'
import {
  $paidServiceFastFilter,
  addFieldToPaidServiceFastFilter,
  setPaidServiceFastFilter
} from '../../../modules/PaidServiceFastFilter/store/paidServiceFastFilter'
import { MY_IN_WORK_UNREAD_OPTIONS } from '../../../modules/PaidServiceFastFilter/consts'

export { $mainTable }

export const $page = createStore(1)
export const setPage = createEvent<number>()
$page.on(setPage, (_, page) => page)

const $limit = createStore(10)

const $documentRegistrationNumber = createStore<string>('')
const setDocumentRegistrationNumber = createEvent<string>()
$documentRegistrationNumber.on(
  setDocumentRegistrationNumber,
  (_, value) => value
)

const $typeId = createStore<number[]>([])
const setTypeId = createEvent<number[]>()
$typeId.on(setTypeId, (_, value) => value)

const $minDate = createStore<string | null>(
  dayjs().subtract(100, 'year')?.format('YYYY-MM-DD') ?? null
)
const setMinDate = createEvent<string | null>()
$minDate.on(setMinDate, (_, value) => value)

const $maxDate = createStore<string | null>(
  dayjs()?.format('YYYY-MM-DD') ?? null
)
const setMaxDate = createEvent<string | null>()
$maxDate.on(setMaxDate, (_, value) => value)

const $contragents = createStore<any>(null)
const setContragents = createEvent<any>()
$contragents.on(setContragents, (_, value) => value)

const $responsibles = createStore<any>(null)
const setResponsibles = createEvent<any>()
$responsibles.on(setResponsibles, (_, value) => value)

const $isEmergency = createStore<boolean | null>(null)
const setIsEmergency = createEvent<boolean | null>()
$isEmergency.on(setIsEmergency, (_, value) => value)

const $needsCalculation = createStore<boolean | null>(null)
const setNeedsCalculation = createEvent<boolean | null>()
$needsCalculation.on(setNeedsCalculation, (_, value) => value)

const $isMunicipal = createStore<boolean | null>(null)
const setIsMunicipal = createEvent<boolean | null>()
$isMunicipal.on(setIsMunicipal, (_, value) => value)

const $clientsClearFlag = createStore(false)
const $usersClearFlag = createStore(false)

const clearFilterStores = createEvent()
$documentRegistrationNumber.on(clearFilterStores, () => '')
$typeId.on(clearFilterStores, () => [])
$isEmergency.on(clearFilterStores, () => null)
$needsCalculation.on(clearFilterStores, () => null)
$isMunicipal.on(clearFilterStores, () => null)
$contragents.on(clearFilterStores, () => null)
$responsibles.on(clearFilterStores, () => null)
$minDate.on(clearFilterStores, () => null)
$maxDate.on(clearFilterStores, () => null)
$clientsClearFlag.on(clearFilterStores, (value) => !value)
$usersClearFlag.on(clearFilterStores, (value) => !value)
$page.on(clearFilterStores, () => 1)

const search = createEvent<{ page?: number }>()

const $filter = sample({
  source: {
    documentRegistrationNumber: $documentRegistrationNumber,
    typeId: $typeId,
    minDate: $minDate,
    maxDate: $maxDate,
    contragents: $contragents,
    responsibles: $responsibles,
    isEmergency: $isEmergency,
    needsCalculation: $needsCalculation,
    isMunicipal: $isMunicipal,
    paidServiceFastFilter: $paidServiceFastFilter,
    page: $page,
    limit: $limit
  }
})

sample({
  clock: search,
  source: $filter,
  fn: (filter, clock) => {
    const page = clock.page ?? filter.page
    const data: any = {
      documentRegistrationNumber:
        filter.documentRegistrationNumber || undefined,
      page,
      limit: filter.limit,
      taskTypeIds: filter.typeId.length ? filter.typeId : undefined,
      createdAt: {
        min: filter.minDate,
        max: filter.maxDate
      },
      clientId: filter.contragents?.id,
      userId: filter.responsibles?.id,
      myInWork: filter.paidServiceFastFilter?.myInWork ?? undefined,
      myInWorkUnread: filter.paidServiceFastFilter?.myInWorkUnread ?? undefined
    }

    if (filter.isEmergency !== null) {
      data.isEmergency = filter.isEmergency
    }
    if (filter.needsCalculation !== null) {
      data.needsCalculation = filter.needsCalculation
    }
    if (filter.isMunicipal !== null) {
      data.isMunicipal = filter.isMunicipal
    }

    return data
  },
  target: getMainTableFx
})

// Ключ для хранения состояния фильтра между переходами по страницам.
// Данные сохраняются до явного сброса через кнопку «Сбросить».
const FILTER_LOCAL_STORAGE_KEY = 'paidServicesFilter'

// Полный снимок фильтра, который нужно восстановить при возврате
// со страницы услуги (включая текущую страницу пагинации).
type SavedFilter = {
  documentRegistrationNumber: string
  typeId: number[]
  minDate: string | null
  maxDate: string | null
  contragents: any
  responsibles: any
  isEmergency: boolean | null
  needsCalculation: boolean | null
  isMunicipal: boolean | null
  page: number
}

const getDefaultTypeIds = (taskTypes: any[]): number[] => {
  return taskTypes
    .filter((type) => type.name?.trim() !== 'Завершена')
    .map((type) => type.id)
}

const YES_NO_OPTIONS = [
  { value: '', label: 'Не выбрано' },
  { value: 'true', label: 'Да' },
  { value: 'false', label: 'Нет' }
]

const getYesNoValue = (value: boolean | null): string => {
  if (value === true) return 'true'
  if (value === false) return 'false'
  return ''
}

const parseYesNoValue = (value: string): boolean | null => {
  if (value === 'true') return true
  if (value === 'false') return false
  return null
}

export default function PaidServicesFilter() {
  const documentRegistrationNumber = useStore($documentRegistrationNumber)
  const typeId = useStore($typeId)
  const minDate = useStore($minDate)
  const maxDate = useStore($maxDate)
  const contragents = useStore($contragents)
  const responsibles = useStore($responsibles)
  const isEmergency = useStore($isEmergency)
  const needsCalculation = useStore($needsCalculation)
  const isMunicipal = useStore($isMunicipal)
  const clientsClearFlag = useStore($clientsClearFlag)
  const usersClearFlag = useStore($usersClearFlag)
  const paidServiceFastFilter = useStore($paidServiceFastFilter)

  const page = useStore($page)
  const usersTable = useStore($userSearch)
  const clientsTable = useStore($clientSearch)
  const taskTypes = useStore($taskTypes) as any[]

  const users = usersTable.map(({ name, id }: any) => {
    return { title: name, id: id }
  })
  const clients = clientsTable.map(({ taxPayerId, name, id }: any) => {
    return {
      title: `${name} ${taxPayerId ? `(ИНН: ${taxPayerId})` : ' '}`,
      id: id
    }
  })
  const usersLoading = users.length === 0
  const clientsLoading = clients.length === 0

  const searchUser = React.useCallback(
    debounce((event, value) => {
      searchUserFx(value)
    }, 200),
    []
  )

  const searchClient = React.useCallback(
    debounce((event, value) => {
      searchClientFx(value)
    }, 200),
    []
  )

  React.useEffect(() => {
    getTaskTypesFx()
  }, [])

  // Даты дополнительно хранятся в sessionStorage для совместимости.
  // Приоритетнее данные из FILTER_LOCAL_STORAGE_KEY (см. инициализацию ниже).
  React.useEffect(() => {
    const savedMinDate = sessionStorage.getItem('minDate')
    const savedMaxDate = sessionStorage.getItem('maxDate')
    if (savedMinDate) setMinDate(savedMinDate)
    if (savedMaxDate) setMaxDate(savedMaxDate)
  }, [])

  const skipNextPageSearchRef = React.useRef(false)
  // Подавляет автосохранение фильтра при сбросе, иначе useEffect
  // перезапишет localStorage сразу после его очистки.
  const skipNextSaveRef = React.useRef(false)
  // Подавляет поиск по изменению быстрого фильтра "Мои" во время его сброса
  // кнопкой «Сбросить» (handleClearFilter сам запускает поиск).
  const skipFastFilterSearchRef = React.useRef(false)
  const isFilterInitializedRef = React.useRef(false)

  // Восстанавливаем фильтр из localStorage после загрузки типов задач.
  // Все поля (включая пагинацию) восстанавливаются здесь, чтобы при
  // возврате со страницы услуги пользователь видел тот же список.
  React.useEffect(() => {
    if (isFilterInitializedRef.current || taskTypes.length === 0) return

    // Подавляем автосохранение и лишний поиск по изменению page,
    // чтобы инициализация не приводила к дублирующим запросам.
    skipNextSaveRef.current = true
    skipNextPageSearchRef.current = true

    const savedFilter = localStorage.getItem(FILTER_LOCAL_STORAGE_KEY)
    if (savedFilter) {
      try {
        const parsed: SavedFilter = JSON.parse(savedFilter)
        setDocumentRegistrationNumber(parsed.documentRegistrationNumber ?? '')
        setTypeId(parsed.typeId ?? getDefaultTypeIds(taskTypes))
        setMinDate(parsed.minDate ?? null)
        setMaxDate(parsed.maxDate ?? null)
        setContragents(parsed.contragents ?? null)
        setResponsibles(parsed.responsibles ?? null)
        setIsEmergency(parsed.isEmergency ?? null)
        setNeedsCalculation(parsed.needsCalculation ?? null)
        setIsMunicipal(parsed.isMunicipal ?? null)
        setPage(parsed.page ?? 1)
      } catch {
        setTypeId(getDefaultTypeIds(taskTypes))
      }
    } else {
      setTypeId(getDefaultTypeIds(taskTypes))
    }

    isFilterInitializedRef.current = true
    // Единственный поиск при инициализации — с восстановленной страницей.
    search({ page: $page.getState() })
  }, [taskTypes])

  // Запрашиваем данные при смене страницы пагинации.
  // skipNextPageSearchRef позволяет избежать лишнего запроса при
  // программной смене страницы (сброс/применение фильтра).
  React.useEffect(() => {
    if (!isFilterInitializedRef.current) return
    if (skipNextPageSearchRef.current) {
      skipNextPageSearchRef.current = false
      return
    }
    search({ page })
  }, [page])

  // Переключение быстрого фильтра "Мои" действует как применение фильтра:
  // возвращаемся на первую страницу и запрашиваем данные.
  React.useEffect(() => {
    if (!isFilterInitializedRef.current) return
    if (skipFastFilterSearchRef.current) {
      skipFastFilterSearchRef.current = false
      return
    }
    skipNextPageSearchRef.current = true
    setPage(1)
    search({ page: 1 })
  }, [paidServiceFastFilter])

  // Сохраняет полный снимок фильтра в localStorage.
  // Вызывается явно из обработчиков и автоматически при изменениях.
  const saveFilterToLocalStorage = () => {
    const filter: SavedFilter = {
      documentRegistrationNumber,
      typeId,
      minDate,
      maxDate,
      contragents,
      responsibles,
      isEmergency,
      needsCalculation,
      isMunicipal,
      page
    }
    localStorage.setItem(FILTER_LOCAL_STORAGE_KEY, JSON.stringify(filter))
  }

  // Автосохранение фильтра при любом изменении поля или страницы.
  // Гарантирует, что переход в услугу и обратно не сбросит состояние.
  // skipNextSaveRef используется, чтобы не пересохранить фильтр
  // во время его сброса (handleClearFilter).
  React.useEffect(() => {
    if (!isFilterInitializedRef.current) return
    if (skipNextSaveRef.current) {
      skipNextSaveRef.current = false
      return
    }
    saveFilterToLocalStorage()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    documentRegistrationNumber,
    typeId,
    minDate,
    maxDate,
    contragents,
    responsibles,
    isEmergency,
    needsCalculation,
    isMunicipal,
    page
  ])

  // Полный сброс фильтра: очищаем localStorage/sessionStorage,
  // сбрасываем сторы и возвращаем стадии к значениям по умолчанию.
  const handleClearFilter = () => {
    skipNextSaveRef.current = true
    sessionStorage.removeItem('minDate')
    sessionStorage.removeItem('maxDate')
    localStorage.removeItem(FILTER_LOCAL_STORAGE_KEY)
    skipNextPageSearchRef.current = true
    skipFastFilterSearchRef.current = true
    clearFilterStores()
    setTypeId(getDefaultTypeIds(taskTypes))
    setPaidServiceFastFilter({})
    search({ page: 1 })
  }

  // Применение фильтра: сбрасываем на первую страницу, сохраняем
  // фильтр и выполняем поиск с первой страницы.
  const handleApplyFilter = () => {
    skipNextPageSearchRef.current = true
    setPage(1)
    saveFilterToLocalStorage()
    search({ page: 1 })
  }

  const handleStageToggle = (id: number) => {
    if (typeId.includes(id)) {
      setTypeId(typeId.filter((item) => item !== id))
    } else {
      setTypeId([...typeId, id])
    }
  }

  const renderYesNoSelect = (
    label: string,
    value: boolean | null,
    onChange: (value: boolean | null) => void
  ) => (
    <Box display="flex" flexDirection="column">
      <Typography width={120} mb={1} variant="subtitle1" fontWeight={600}>
        {label}:
      </Typography>
      <FormControl sx={{ width: 130 }}>
        <Select
          value={getYesNoValue(value)}
          onChange={(event: SelectChangeEvent<string>) =>
            onChange(parseYesNoValue(event.target.value))
          }
        >
          {YES_NO_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  )

  const renderUnreadSelect = () => (
    <Box display="flex" flexDirection="column">
      <Typography width={120} mb={1} variant="subtitle1" fontWeight={600}>
        Непрочитанные:
      </Typography>
      <FormControl sx={{ width: 130 }}>
        <Select
          value={
            paidServiceFastFilter?.myInWorkUnread != null
              ? String(paidServiceFastFilter.myInWorkUnread)
              : ''
          }
          onChange={(event: SelectChangeEvent<string>) =>
            addFieldToPaidServiceFastFilter({
              myInWorkUnread: event.target.value
                ? Number(event.target.value)
                : undefined
            })
          }
        >
          <MenuItem value="">
            <em>Все</em>
          </MenuItem>
          {MY_IN_WORK_UNREAD_OPTIONS.map(({ id, label, value }) => (
            <MenuItem key={id} value={String(value)}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  )

  return (
    <Box mt={3} mb={0}>
      <Paper sx={{ padding: '1rem', borderRadius: '1rem' }} elevation={0}>
        <Accordion sx={{ border: '1px solid #e6e6e6', boxShadow: 0 }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1a-content"
            id="panel1a-header"
          >
            <Typography>Фильтр</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pb: 3, pt: 0 }}>
            <Box display="flex" flexWrap="wrap" gap={5}>
              <Box
                display="flex"
                flexDirection="column"
                gap={3}
                flex={1}
                minWidth={300}
              >
                <Box display="flex" flexDirection="column">
                  <Typography
                    width={110}
                    mb={1}
                    variant="subtitle1"
                    fontWeight={600}
                  >
                    Номер:
                  </Typography>
                  <TextField
                    id="outlined-basic"
                    variant="outlined"
                    value={documentRegistrationNumber}
                    //@ts-ignore
                    onChange={(e) =>
                      setDocumentRegistrationNumber(e.target.value)
                    }
                    sx={{ width: 260 }}
                  />
                </Box>
                <Box display="flex" flexDirection="column">
                  <Typography
                    pr={2}
                    mb={1}
                    variant="subtitle1"
                    fontWeight={600}
                  >
                    Дата заявки:
                  </Typography>
                  <Box
                    display="flex"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={2}
                  >
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        inputFormat="DD.MM.YYYY"
                        label="С"
                        value={minDate}
                        onChange={(newValue) => {
                          const formatted =
                            //@ts-ignore
                            newValue?.format('YYYY-MM-DD') ?? null
                          setMinDate(formatted)
                          if (formatted) {
                            sessionStorage.setItem('minDate', formatted)
                          } else {
                            sessionStorage.removeItem('minDate')
                          }
                        }}
                        renderInput={(params) => (
                          <TextField sx={{ width: '260px' }} {...params} />
                        )}
                      />
                    </LocalizationProvider>
                    <Typography variant="subtitle2">-</Typography>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        inputFormat="DD.MM.YYYY"
                        label="По"
                        value={maxDate}
                        onChange={(newValue) => {
                          const formatted =
                            //@ts-ignore
                            newValue?.format('YYYY-MM-DD') ?? null
                          setMaxDate(formatted)
                          if (formatted) {
                            sessionStorage.setItem('maxDate', formatted)
                          } else {
                            sessionStorage.removeItem('maxDate')
                          }
                        }}
                        renderInput={(params) => (
                          <TextField sx={{ width: '260px' }} {...params} />
                        )}
                      />
                    </LocalizationProvider>
                  </Box>
                </Box>
                <Box display="flex" flexDirection="column">
                  <Typography
                    width={110}
                    mb={1}
                    variant="subtitle1"
                    fontWeight={600}
                  >
                    Контрагент:
                  </Typography>
                  <Autocomplete
                    //@ts-ignore
                    key={clientsClearFlag}
                    id="tags-standard"
                    options={clients}
                    // value делает поле контролируемым: при возврате на страницу
                    // восстановленное значение отобразится, даже если список
                    // options ещё не загружен.
                    value={contragents || null}
                    loading={clientsLoading}
                    loadingText="Загрузка..."
                    noOptionsText={'Ничего не найдено'}
                    onInputChange={searchClient}
                    //@ts-ignore
                    onChange={(event, value) => setContragents(value)}
                    getOptionLabel={(option) => option?.title}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    renderInput={(params) => (
                      <TextField sx={{ width: '260px' }} {...params} />
                    )}
                  />
                </Box>

                <Stack direction={'row'} gap={2} alignItems={'end'}>

                <Box display="flex" flexDirection="column">
                  <Typography
                    width={110}
                    mb={1}
                    variant="subtitle1"
                    fontWeight={600}
                  >
                    Ответственный:
                  </Typography>
                  <Autocomplete
                    //@ts-ignore
                    key={usersClearFlag}
                    id="tags-standard"
                    options={users}
                    // value делает поле контролируемым: при возврате на страницу
                    // восстановленное значение отобразится, даже если список
                    // options ещё не загружен.
                    value={responsibles || null}
                    loading={usersLoading}
                    loadingText="Загрузка..."
                    onInputChange={searchUser}
                    noOptionsText={'Ничего не найдено'}
                    //@ts-ignore
                    onChange={(event, value) => setResponsibles(value)}
                    getOptionLabel={(option) => option?.title}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    renderInput={(params) => (
                      <TextField sx={{ width: '260px' }} {...params} />
                    )}
                  />
                </Box>

                <Stack direction={'row'} gap={2}>
                  {renderYesNoSelect('Авария', isEmergency, setIsEmergency)}
                  {renderYesNoSelect(
                    'Калькуляция',
                    needsCalculation,
                    setNeedsCalculation
                  )}
                  {renderYesNoSelect(
                    'Бюджетники',
                    isMunicipal,
                    setIsMunicipal
                  )}
                  {renderUnreadSelect()}
                </Stack>

                </Stack>

                <Stack direction={'row'} spacing={2} mt={2}>
                  <Button
                    variant="outlined"
                    color={'error'}
                    onClick={handleClearFilter}
                  >
                    Сбросить
                  </Button>
                  <Button variant="contained" onClick={handleApplyFilter}>
                    Применить
                  </Button>
                </Stack>
              </Box>
              <Box flex={1} minWidth={300}>
                <Typography
                  mb={2}
                  variant="subtitle1"
                  fontWeight={600}
                >
                  Стадия:
                </Typography>
                <Box
                  sx={{
                    columnCount: 2,
                    columnGap: '24px',
                    '@media (max-width: 900px)': {
                      columnCount: 1
                    }
                  }}
                >
                  {taskTypes.length > 0 &&
                    taskTypes.map(({ name, id }: any) => (
                      <FormControlLabel
                        key={id}
                        control={
                          <Checkbox
                            checked={typeId.includes(id)}
                            onChange={() => handleStageToggle(id)}
                          />
                        }
                        label={name}
                        sx={{
                          display: 'flex',
                          breakInside: 'avoid',
                          mb: 0.5
                        }}
                      />
                    ))}
                </Box>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Paper>
    </Box>
  )
}
